import theme from '@/shared/theme';
import { CategoryNames } from '../types/catalog-category-data';
import { CourseType } from '../types/course';
import { ICourseData } from '../types/course-data';
import { CourseDataFilters } from '../types/filtered-data';
import { Instrument } from '../types/instrument';
import { CardColorsMetadata, CourseData, CourseHighlight } from '@/shared/types';

const setImportant = (string: string) => `${string} !important`;

const colorMapping: {
  highlightedCourses: Record<CourseType, CardColorsMetadata>;
  regularCourses: Record<CourseType, CardColorsMetadata>;
  isCourseStarted: CardColorsMetadata;
} = {
  highlightedCourses: {
    serie: {
      cardBg: theme.palette.primary.dark,
      courseTypeChipBg: theme.palette.primary.contrastText,
      courseTypeChipTextColor: theme.palette.primary.dark,
      cardTextColor: theme.palette.primary.contrastText,
      courseLevelChipBgColor: theme.palette.primary.contrastText,
      courseLevelChipTextColor: theme.palette.primary.main,
      genreChipColor: theme.palette.primary.contrastText,
    },
    percorsi: {
      cardBg: theme.palette.complementary.main,
      courseTypeChipBg: theme.palette.primary.contrastText,
      courseTypeChipTextColor: '#C88D06',
      cardTextColor: '#412107',
      courseLevelChipBgColor: '#412107',
      courseLevelChipTextColor: theme.palette.complementary.main,
      genreChipColor: '#412107',
    },
    masterclass: {
      cardBg: theme.palette.secondary.main,
      courseTypeChipBg: theme.palette.primary.contrastText,
      courseTypeChipTextColor: theme.palette.secondary.dark,
      cardTextColor: theme.palette.secondary.contrastText,
      courseLevelChipBgColor: theme.palette.secondary.contrastText,
      courseLevelChipTextColor: theme.palette.secondary.main,
      genreChipColor: theme.palette.secondary.contrastText,
    },
  },
  regularCourses: {
    serie: {
      cardBg: theme.palette.paper.main,
      courseTypeChipBg: theme.palette.primary.main,
      courseTypeChipTextColor: theme.palette.primary.contrastText,
      cardTextColor: theme.palette.primary.contrastText,
      courseLevelChipBgColor: theme.palette.neutral.main,
      courseLevelChipTextColor: theme.palette.primary.contrastText,
      genreChipColor: theme.palette.primary.contrastText,
    },
    percorsi: {
      cardBg: theme.palette.paper.main,
      courseTypeChipBg: theme.palette.complementary.main,
      courseTypeChipTextColor: '#412107',
      cardTextColor: theme.palette.primary.contrastText,
      courseLevelChipBgColor: theme.palette.neutral.main,
      courseLevelChipTextColor: theme.palette.primary.contrastText,
      genreChipColor: theme.palette.primary.contrastText,
    },
    masterclass: {
      cardBg: theme.palette.paper.main,
      courseTypeChipBg: theme.palette.secondary.main,
      courseTypeChipTextColor: theme.palette.secondary.contrastText,
      cardTextColor: theme.palette.primary.contrastText,
      courseLevelChipBgColor: theme.palette.neutral.main,
      courseLevelChipTextColor: theme.palette.primary.contrastText,
      genreChipColor: theme.palette.primary.contrastText,
    },
  },
  isCourseStarted: {
    cardBg: theme.palette.paper.main,
    courseTypeChipBg: theme.palette.primary.main,
    courseTypeChipTextColor: theme.palette.primary.contrastText,
    cardTextColor: theme.palette.primary.contrastText,
    courseLevelChipBgColor: theme.palette.primary.contrastText,
    courseLevelChipTextColor: theme.palette.primary.contrastText,
    genreChipColor: '',
  },
};

const applyImportantToMetadata = (metadata: CardColorsMetadata): CardColorsMetadata => ({
  cardBg: metadata.cardBg,
  courseTypeChipBg: setImportant(metadata.courseTypeChipBg),
  courseTypeChipTextColor: setImportant(metadata.courseTypeChipTextColor),
  cardTextColor: setImportant(metadata.cardTextColor),
  courseLevelChipBgColor: setImportant(metadata.courseLevelChipBgColor),
  courseLevelChipTextColor: setImportant(metadata.courseLevelChipTextColor),
  genreChipColor: setImportant(metadata.genreChipColor),
});

export const getCardColors = (
  courseType?: CourseType,
  courseHighlight?: boolean,
  isCoureStarted?: boolean,
): CardColorsMetadata | undefined => {
  if (isCoureStarted) {
    return applyImportantToMetadata(colorMapping.isCourseStarted);
  }
  const type: CourseType = courseType
    ? (courseType.toLowerCase() as CourseType)
    : ('serie' as CourseType);
  const highlightType: CourseHighlight = courseHighlight ? 'highlightedCourses' : 'regularCourses';
  if (!courseType) return undefined;

  const metadata = colorMapping[highlightType]?.[type];
  return metadata ? applyImportantToMetadata(metadata) : undefined;
};

export const getInstructorName = (instructors: CourseData['instructors']) => {
  if (!instructors) return '';
  if (instructors.length === 1) {
    return instructors[0].name;
  } else if (instructors.length > 1) {
    return `${instructors.length} Insegnanti`;
  } else {
    return '';
  }
};

export function getChildInstruments(parent: any, allInstruments: any[]): any[] {
  return allInstruments.filter(
    (inst) =>
      inst.parentInstrument && inst.parentInstrument.instrumentName === parent.instrumentName,
  );
}

export function instrumentHasChildren(parent: any, allInstruments: any[]): boolean {
  return getChildInstruments(parent, allInstruments).length > 0;
}

export function hasActiveFilters(filters: CourseDataFilters): boolean {
  return (
    filters.instruments.length > 0 ||
    filters.skillLevels.length > 0 ||
    filters.genres.length > 0 ||
    filters.topics.length > 0 ||
    filters.types.length > 0
  );
}

export function toggleFilterValue(
  currentValues: string[],
  value: string,
  checked: boolean,
): string[] {
  return checked
    ? Array.from(new Set([...currentValues, value]))
    : currentValues.filter((v) => v !== value);
}

export function updateInstrumentsFilter(
  checked: boolean,
  instrument: Instrument,
  currentInstruments: string[],
  allInstruments: Instrument[],
): string[] {
  const isParent = !instrument.parentInstrument;
  let newInstruments = [...currentInstruments];

  if (isParent) {
    const children = getChildInstruments(instrument, allInstruments).map(
      (child) => child.instrumentName,
    );
    newInstruments = checked
      ? Array.from(new Set([...newInstruments, instrument.instrumentName, ...children]))
      : newInstruments.filter((i) => i !== instrument.instrumentName && !children.includes(i));
  } else {
    newInstruments = toggleFilterValue(newInstruments, instrument.instrumentName, checked);
  }

  return newInstruments;
}

export const parseCategoryName = (categoryName: CategoryNames) => {
  if (categoryName === 'da-zero') return 'Percorsi da zero';
  if (categoryName === 'piu-recenti') return 'Ultime uscite';
  if (categoryName === 'piu-visti') return 'Più seguiti';
  if (categoryName === 'masterclass') return 'Masterclass';
  return 'Categoria non trovata';
};

/**
 * Helper function to determine if two arrays of ICourseData have identical items based on their unique IDs.
 * @param prev - The previous array of ICourseData.
 * @param newData - The new array of ICourseData to be compared.
 * @returns A boolean indicating whether both arrays contain the same items.
 */
export const areCourseDataArraysEqual = (prev: ICourseData[], newData: ICourseData[]): boolean => {
  if (prev.length !== newData.length) return false;

  const prevIds = new Set(prev.map((item) => item.title)); // Assuming each item has a unique 'id' property
  for (const item of newData) {
    if (!prevIds.has(item.title)) {
      return false;
    }
  }
  return true;
};
